import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  APP_NAME,
  APP_SUPPORT_EMAIL,
  APP_URL,
  FULL_COMPANY_NAME,
  TAGLINE,
} from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import { APP_TERMS_ROUTE } from "../helpers/routeNames";
import SEO from "../components/SEO";

const APP_PRIVACY_URL = `https://${APP_URL}/privacy`;

export default function TermsOfService({ mustAcceptTos = false }) {
  return (
    <Container $mustAcceptTos={mustAcceptTos}>
      <SEO
        title={`Terms of Service | ${APP_NAME} - ${TAGLINE}`}
        description={`${APP_NAME} Terms of Service - Legal terms and conditions for our AI-powered property management platform used by landlords and property managers.`}
        keywords={`${APP_NAME} terms of service, property management platform terms, landlord software terms, rental property management terms, AI property management`}
        imageAlt={`${APP_NAME} terms of service and legal agreements`}
        category="Legal"
        tags={[
          "LandlordOps terms of service",
          "property management platform terms",
          "landlord software terms",
          "AI property management terms",
        ]}
      />
      <MainHeader>Terms of Service</MainHeader>
      <Spacer $marginTop="2rem" />
      <TextContainer $mustAcceptTos={mustAcceptTos}>
        <TextSection>
          {APP_NAME} Services ("{APP_NAME}" or "Services") are provided by{" "}
          {FULL_COMPANY_NAME} ("We", "Our", "{APP_NAME}") to you ("User" or
          "Users").
        </TextSection>
        <TextSection>
          If you use any of our Services, then you agree to these Terms of
          Services and our Privacy Policy (collectively the "Agreement").
        </TextSection>
        <TextSection>
          For more information about our terms and privacy practices, please
          visit{" "}
          <a
            href={`https://${APP_URL}${APP_TERMS_ROUTE}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://{APP_URL}
            {APP_TERMS_ROUTE}
          </a>{" "}
          and{" "}
          <a
            href={`https://${APP_URL}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            https://{APP_URL}/privacy
          </a>
          .
        </TextSection>
        <TextSection>
          <strong>Last Updated:</strong> July 24, 2026
        </TextSection>

        <Header>Acceptance of the Terms of Service</Header>
        <List>
          <ListItem>
            By using the {APP_NAME} website or any other {APP_NAME} product or
            service provided to you by {APP_NAME} (collectively the "Services"),
            you signify your agreement to: 1) these terms and conditions (the
            "Terms of Service"), and 2) our Privacy Policy (found at{" "}
            <Link to="/privacy">{APP_PRIVACY_URL}</Link>). Although we will
            attempt to notify you when major changes are made to these Terms of
            Service, you should periodically review the most up to date version
            (found at{" "}
            <Link to={APP_TERMS_ROUTE}>
              https://{APP_URL}
              {APP_TERMS_ROUTE}
            </Link>
            ). {APP_NAME} may, in its sole discretion, modify or revise these
            Terms of Service at any time. All modifications to the Agreement
            that we post on the Services will be effective immediately upon
            posting, and your continued use of the Services after we post
            changes to the Agreement will confirm your acceptance of the
            changes. If you do not agree to the modified Agreement, you must
            stop using the Services. Nothing in these Terms of Service shall be
            deemed to confer any third party rights.
          </ListItem>
          <ListItem>
            If you are using our Services on behalf of a business, that business
            accepts these terms. It will hold harmless and indemnify {APP_NAME}{" "}
            and its officers, agents, and employees from any claim, suit or
            action arising from or related to the use of the Services or
            violation of these terms, including any liability or expense arising
            from claims, losses, damages, suits, judgments, litigation costs and
            attorneys' fees.
          </ListItem>
        </List>

        <Header>Legal Compliance</Header>
        <List>
          <ListItem>
            {APP_NAME} is designed for use by landlords, property managers, real
            estate investors, and related property management businesses
            ("Users") to enhance property management efficiency and provide
            AI-powered tools for rent tracking, repair management, tenant
            communication, and operational guidance. When used for processing
            tenant and property information, {APP_NAME} functions as a property
            management service provider under applicable landlord-tenant laws
            and regulations.
          </ListItem>
          <ListItem>
            Users who use our Services to process, store, or transmit tenant
            information, property data, or financial records must comply with
            all applicable federal, state, and local laws governing
            landlord-tenant relationships, fair housing, and property
            management.
          </ListItem>
          <ListItem>
            By using our Services, you acknowledge that you are responsible for
            ensuring compliance with all applicable laws and regulations in your
            jurisdiction, including but not limited to fair housing laws,
            landlord-tenant laws, and data protection regulations.
          </ListItem>
          <ListItem>
            Users are responsible for ensuring they have appropriate
            authorizations and permissions as required by applicable laws before
            uploading or inputting tenant information, property data, or
            financial records into our Services.
          </ListItem>
          <ListItem>
            By submitting tenant and property information to our Services, Users
            represent and warrant that they have obtained appropriate consent
            from tenants and have the legal right to process such information
            for property management purposes.
          </ListItem>
          <ListItem>
            Users acknowledge and agree that they have informed tenants about
            the use of our platform for property management purposes, including
            rent tracking, lease management, and repair coordination, and that
            tenants understand how their information will be used.
          </ListItem>
          <ListItem>
            {APP_NAME} does not provide legal advice and recommends that Users
            consult with qualified legal professionals regarding their specific
            legal obligations and compliance requirements.
          </ListItem>
        </List>

        <Header>Ability to Accept the Terms of Service</Header>
        <List>
          <ListItem>
            To use the Services, you must be at least eighteen (18) years old
            (or the minimum permissible age under the applicable law of your
            jurisdiction, if higher) ("Minimum Age"). The use of the Service by
            anyone under the Minimum Age is unauthorized and unlicensed. By
            using the Service, you represent and warrant that you meet the
            foregoing requirement. If you do not meet this requirement, you may
            not access or use the Service.
          </ListItem>
          <ListItem>
            If you are a property manager or landlord, you represent and warrant
            that you are duly licensed and authorized to manage properties in
            your jurisdiction and to use our Services for professional property
            management purposes. For property management companies, you warrant
            that you operate in compliance with all applicable state and local
            regulations governing property management and tenant relations.
          </ListItem>
        </List>

        <Header>Service</Header>
        <List>
          <ListItem>
            These Terms of Service apply to all users of {APP_NAME} Services,
            including users who are also contributors of Content to the Service.
            "Content" includes the text, software, images, photos, videos,
            property data, tenant information, financial records, lease
            documents, audiovisual combinations, interactive features and other
            Content you may view on, access through, or contribute to the
            Service. The Service includes all aspects of any
            {APP_NAME} product, including but not limited to software and
            services offered via the {APP_NAME} website and mobile applications.
          </ListItem>
          <ListItem>
            The {APP_NAME} platform provides a comprehensive suite of AI-powered
            tools specifically designed for landlords, property managers, and
            real estate investors, including:
            <List>
              <ListItem>
                <strong>Subscription plans and feature scope:</strong> We may
                offer a <strong>Free Gateway</strong> (limited doors, no paid
                subscription), self-serve paid plans such as Starter, Growth,
                and Scale, and <strong>Enterprise</strong> or other custom
                agreements. Current public self-serve plans include the full
                LandlordOps operating system, including OpsChat tenant
                messaging, Leo AI, rent collection, vendor workflows, books
                and Schedule E-style exports, listing and leasing tools, and
                closing-readiness or investor packet features. OpsChat is a
                tenant-facing feature within LandlordOps, not a separate
                subscription product. Public self-serve plans differ primarily
                by door or unit limits. Feature availability may still vary for
                legacy or grandfathered accounts, Enterprise or other custom
                agreements, beta features, third-party service availability,
                and account configuration. We may offer{" "}
                <strong>monthly and annual</strong> billing; taxes and payment
                methods are as shown at checkout or in-product.
              </ListItem>
              <ListItem>
                <strong>Rent Tracking System:</strong> An automated system for
                tracking rent payments, due dates, late fees, and payment
                history across all properties and tenants. The system provides
                real-time updates, automated reminders, and comprehensive
                reporting to help landlords maintain accurate financial records
                and identify payment patterns.
              </ListItem>
              <ListItem>
                <strong>Leasing Guidance:</strong> AI-assisted guidance for
                leasing workflows and common considerations. {APP_NAME} does not
                generate lease documents or provide legal advice.
              </ListItem>
              <ListItem>
                <strong>Repair Management System:</strong> Comprehensive repair
                request tracking and management tools that log maintenance
                requests, generate professional follow-up messages using AI,
                track repair status, and coordinate with contractors. The system
                helps landlords maintain properties efficiently and communicate
                effectively with tenants.
              </ListItem>
              <ListItem>
                <strong>Property Analytics Dashboard:</strong> Comprehensive
                reporting and analytics tools that track property performance
                metrics, rent collection rates, vacancy rates, maintenance
                costs, ROI calculations, and other key performance indicators to
                help landlords optimize their property management and investment
                decisions.
              </ListItem>
              <ListItem>
                <strong>Tenant Management System:</strong> Integrated tenant
                profile management, communication tools, and document storage
                that centralizes all tenant-related information, lease
                agreements, payment history, and communication records in one
                secure platform.
              </ListItem>
              <ListItem>
                <strong>Financial Management Tools:</strong> Automated financial
                tracking, expense management, income reporting, and tax
                preparation assistance that helps landlords maintain accurate
                financial records and optimize their property investments.
              </ListItem>
              <ListItem>
                <strong>Tax Preparation Support:</strong> A data collection and
                transmission tool that allows users to submit rental property
                addresses, income, expenses, and contact information to their
                designated certified public accountant (CPA). The tool formats
                data for Schedule E and transmits it to the CPA via email (with
                the user CC'd). {APP_NAME} does not provide tax advice, prepare
                tax returns, or guarantee the accuracy, completeness, or
                suitability of any data for tax purposes. Users are solely
                responsible for verifying all information with their CPA before
                filing.
              </ListItem>
              <ListItem>
                <strong>Deal Analyzer:</strong> An AI-powered tool that provides
                estimated DSCR, rent, and debt service based on an address or
                listing URL. It may suggest whether to consider a lower or
                higher offer. All estimates and recommendations are for
                informational purposes only and do not constitute investment,
                financial, or legal advice. {APP_NAME} does not guarantee that
                you will obtain any deal, that a property will perform as
                estimated, or any particular outcome. You are solely responsible
                for your own due diligence and investment decisions.
              </ListItem>
              <ListItem>
                <strong>
                  Leo, OpsChat tenant messaging, your AI First Responder, and other AI
                  assistants:
                </strong>{" "}
                In-product AI features (including tenant-facing chat, triage
                before messages reach you, and assistants that help draft
                communications or answer how-to and general informational
                questions) are included in current public self-serve plans.
                Availability may vary based on account configuration, legacy or
                custom agreements, and third-party service availability.
                Outputs are informational only, may be incomplete or incorrect,
                and are not legal, financial, tax, or investment advice. They
                are not a substitute for a licensed attorney or other qualified
                professional. How we process these interactions is described in
                our Privacy Policy.
              </ListItem>
              <ListItem>
                <strong>Product feedback and surveys:</strong> We may offer
                optional feedback widgets, contact forms, or product surveys. You
                choose what to submit. We may use your submissions and limited
                technical information (as described in our Privacy Policy) to
                improve the Services. Submitting feedback does not obligate us to
                respond or to implement any suggestion.
              </ListItem>
              <ListItem>
                <strong>Marketing pages and public experiences:</strong> Certain
                marketing pages, demos, or public listing-related experiences may
                be available with or without an account. Those experiences are
                governed by these Terms and our Privacy Policy.
              </ListItem>
              <ListItem>
                <strong>Public rental applications:</strong> Landlords may share
                a public application link for a listing. Prospects who open that
                link and submit an application use {APP_NAME} as applicants.
                Submitting an application constitutes agreement to these Terms
                and our Privacy Policy for that use of the Services, as
                described further below under &quot;Applicants and rental
                applications.&quot;
              </ListItem>
            </List>
            These services work together to create a seamless property
            management platform designed to help landlords and property managers
            operate more efficiently, support their compliance efforts where
            applicable, and manage their property investments. Current public
            self-serve plans include the full operating system and differ
            primarily by door or unit limits, subject to the availability
            exceptions described above.
          </ListItem>
          <ListItem>
            Listings data, Leo chat transcripts, and any private instructions
            you provide for Leo's pre-screening are considered landlord-managed
            content. You control who sees that content and when it is sent to
            such third-party partners that assist with tenant screening,
            background checks, or identity verification. When you trigger a
            screening workflow (e.g., running a credit or criminal background
            report through an optional provider), you authorize {APP_NAME} to
            transmit the necessary information to that provider and acknowledge
            that the provider’s terms and privacy policies will also apply.
          </ListItem>
          <ListItem>
            The Service may contain links to third party websites that are not
            owned or controlled by {APP_NAME}. {APP_NAME} has no control over,
            and assumes no responsibility for, the content, privacy policies, or
            practices of any third party websites. In addition, {APP_NAME} will
            not and cannot censor or edit the content of any third-party site.
            By using the Service, you expressly relieve {APP_NAME} from any and
            all liability arising from your use of any third-party website.
            Third-party integrations (such as payment processors like Stripe,
            property management software, accounting systems, and legal
            services) are subject to their respective terms and privacy
            policies.
          </ListItem>
          <ListItem>
            Accordingly, we encourage you to be aware when you leave a{" "}
            {APP_NAME} Service and to read the terms and conditions and other
            policies of each other website that you visit.
          </ListItem>
        </List>

        <Header>Applicants and rental applications</Header>
        <List>
          <ListItem>
            If you are a prospect or applicant using a public rental application
            form hosted by {APP_NAME}, you agree to these Terms and our Privacy
            Policy by checking the required acknowledgment and submitting the
            application. You represent that the information and documents you
            provide are accurate and that you are authorized to submit them.
          </ListItem>
          <ListItem>
            Application information (including identity, contact, income,
            rental history, and uploaded documents such as photo ID or paystubs)
            is collected so {APP_NAME} can deliver it to the landlord associated
            with the listing, store it in that landlord&apos;s lease-up /
            onboarding tools, and send related transactional notices (for
            example, confirmation or landlord notification emails). Details of
            that processing are described in our Privacy Policy.
          </ListItem>
          <ListItem>
            {APP_NAME} provides the application technology as a platform
            service. The landlord—not {APP_NAME}—is responsible for evaluating
            applications, complying with fair housing and screening laws, and
            deciding whether to offer a lease. Background or credit checks, if
            any, are typically performed by the landlord or by third-party
            providers the landlord selects; those providers&apos; terms and
            privacy policies may also apply.
          </ListItem>
          <ListItem>
            Submitting an application does not create a tenancy, guarantee a
            lease, or create a contractual relationship with the landlord beyond
            what the landlord separately offers. {APP_NAME} does not guarantee
            that a landlord will review, respond to, or accept any application.
          </ListItem>
          <ListItem>
            Landlords who enable application links are responsible for the
            accuracy of listing details they publish, for obtaining any
            consents required for their screening practices, and for using
            applicant data only for legitimate rental-selection and property
            management purposes consistent with applicable law and our Privacy
            Policy.
          </ListItem>
        </List>

        <Header>Referral and Partner Programs</Header>
        <List>
          <ListItem>
            From time to time, {APP_NAME} may provide referrals or introductions
            to third-party professionals such as lenders, loan officers,
            mortgage brokers, contractors, handymen, property maintenance
            providers, real estate agents or brokers, insurance providers,
            accountants, attorneys, and other vendors (collectively,
            "Partners"). These referrals are provided as a convenience to Users.
          </ListItem>
          <ListItem>
            You acknowledge and agree that Partners are independent third
            parties and are not employees, agents, or representatives of{" "}
            {APP_NAME}. {APP_NAME} does not control, supervise, or direct the
            work of any Partner and is not responsible for the quality,
            timeliness, pricing, legality, licensing, insurance, or outcomes of
            any services provided by Partners.
          </ListItem>
          <ListItem>
            No Legal, Brokerage, or Advisory Services: Any information we
            present about Partners is informational only and does not constitute
            legal, financial, investment, or brokerage advice. You are solely
            responsible for conducting your own due diligence before engaging
            any Partner, including verifying licenses, insurance, references,
            and suitability for your needs.
          </ListItem>
          <ListItem>
            Licensing and Compliance: You are responsible for ensuring that any
            Partner you select holds all licenses and permissions required by
            applicable federal, state, and local laws, including but not limited
            to real estate brokerage, lending, and contractor licensing rules.
            Use of a Partner is at your own risk and subject to any separate
            terms you establish with that Partner.
          </ListItem>
          <ListItem>
            Compensation Disclosures: {APP_NAME} may receive compensation,
            referral fees, revenue share, commissions, or other consideration in
            connection with referrals or introductions to Partners, including
            but not limited to landlords, property managers, real estate agents
            and brokers, contractors and handymen, maintenance vendors, lenders
            and loan officers, insurers, and other real estate professionals.
            Any such compensation does not increase the price you pay to
            Partners. Where required by law (for example, under federal or state
            real estate or lending laws), we will make additional written
            disclosures at the time of referral.
          </ListItem>
          <ListItem>
            No Endorsement or Warranty: Listing or referring a Partner does not
            constitute an endorsement or warranty by {APP_NAME}. We make no
            guarantees regarding availability, pricing, performance, or
            outcomes.
          </ListItem>
          <ListItem>
            Information Sharing for Referrals: If you click a referral link,
            request to be contacted, or otherwise ask us to connect you with a
            Partner, you authorize {APP_NAME} to share the minimal necessary
            information with that Partner to facilitate the referral (for
            example, your name, contact information, and high-level property
            details). Such sharing will be handled in accordance with our
            Privacy Policy.
          </ListItem>
          <ListItem>
            Tax Preparation and CPA Data Sharing: When you use our tax
            preparation feature, you designate a CPA (by URL or selection) to
            receive your rental property data. By submitting tax prep
            information, you authorize {APP_NAME} to transmit that data
            (including property addresses, income, expenses, and your contact
            information) to your designated CPA via email. Your CPA is an
            independent third-party Partner. We do not control, supervise, or
            verify the services provided by your CPA. You are solely responsible
            for the accuracy of the data you submit and for any use your CPA
            makes of it.
          </ListItem>
          <ListItem>
            No Agency Relationship Created: Your use of any Partner does not
            create a joint venture, partnership, or agency relationship between
            you and {APP_NAME}. We are not a party to, and are not responsible
            for, any agreements between you and a Partner.
          </ListItem>
          <ListItem>
            Disputes with Partners: Any disputes or claims arising from services
            provided by a Partner are solely between you and that Partner. You
            agree to hold {APP_NAME} harmless from and against any claims,
            damages, liabilities, costs, or losses arising from your engagement
            of a Partner.
          </ListItem>
        </List>

        <Header>{APP_NAME} User Accounts</Header>
        <List>
          <ListItem>
            In order to access {APP_NAME} Services, you will have to create a{" "}
            {APP_NAME} user account. You may never use another's user account
            without permission. When creating your user account, you must
            provide accurate and complete information. You are solely
            responsible for the activity that occurs on your account, and you
            must keep your account password secure. You must notify {APP_NAME}{" "}
            immediately of any breach of security or unauthorized use of your
            account.
          </ListItem>
          <ListItem>
            For property managers and landlords, we may require verification of
            your professional credentials before granting full access to certain
            features of the Services. You agree to provide accurate and current
            information regarding your professional status, licensing, and
            property portfolio.
          </ListItem>
          <ListItem>
            Although {APP_NAME} will not be liable for your losses caused by any
            unauthorized use of your account, you may be liable for the losses
            of {APP_NAME} or others due to such unauthorized use.
          </ListItem>
        </List>

        <Header>Fees, Billing, and Payment</Header>
        <List>
          <ListItem>
            Some features of the Services may require a paid subscription, add-on,
            or one-time purchase. Fees, billing cycles, taxes, and accepted payment
            methods are disclosed at checkout, in-product, or in your account and
            may change from time to time with notice as required by applicable
            law.
          </ListItem>
          <ListItem>
            Payments are processed by third-party payment processors (including
            without limitation Stripe, PayPal, or other providers identified at
            the point of purchase). By submitting payment information, you
            authorize charges for the amounts and intervals applicable to your
            selections and agree to the applicable processor’s terms and privacy
            policy.
          </ListItem>
          <ListItem>
            Purchases made through an app store or third-party marketplace are
            also subject to that platform’s terms and payment rules.
          </ListItem>
          <ListItem>
            Refunds, cancellations, and plan changes depend on the offer or plan
            you select; where required by law, you may have additional rights.
            Review the billing screens, checkout disclosures, and account
            settings applicable to your purchase.
          </ListItem>
        </List>

        <Header>Referral and Agent Partnership Programs</Header>
        <TextSection>
          {APP_NAME} offers referral programs for users and real estate agents
          to earn rewards for successful referrals. By participating in these
          programs, you agree to the following terms:
        </TextSection>
        <List>
          <ListItem>
            <strong>User Referral Program:</strong> Users may earn free months
            of service for successful referrals. Each referred user must sign up
            using your unique referral link and complete their first payment.
            Rewards are credited to your account within 30 days of the referred
            user's first payment.
          </ListItem>
          <ListItem>
            <strong>Real Estate Agent Partnership Program:</strong> Licensed
            real estate agents may participate in our agent partnership program
            to earn commissions and provide discounts to their clients. Agents
            must be verified and approved before participating.
          </ListItem>
          <ListItem>
            <strong>Commission Structure:</strong> Agents earn $500 for each
            successful referral where the referred client signs up and pays for
            their first month of service. Commissions are paid within 30 days of
            the client's first payment.
          </ListItem>
          <ListItem>
            <strong>Agent Discounts:</strong> Approved agents receive a unique
            discount code (AGENT25) that provides their clients with a 25%
            discount on their first year of service.
          </ListItem>
          <ListItem>
            <strong>Referral Tracking:</strong> All referrals must be tracked
            through our official referral system. Referrals made outside of this
            system are not eligible for rewards or commissions.
          </ListItem>
          <ListItem>
            <strong>Fraud Prevention:</strong> {APP_NAME} reserves the right to
            investigate and disqualify referrals that appear fraudulent,
            including but not limited to self-referrals, fake accounts, or
            manipulation of the referral system.
          </ListItem>
          <ListItem>
            <strong>Program Changes:</strong> {APP_NAME} reserves the right to
            modify, suspend, or terminate referral programs at any time with 30
            days' notice to participants.
          </ListItem>
          <ListItem>
            <strong>Tax Responsibility:</strong> Participants are responsible
            for reporting and paying any applicable taxes on rewards or
            commissions received through our referral programs.
          </ListItem>
        </List>

        <Header>General Use of the Service</Header>
        <TextSection>
          {APP_NAME} hereby grants you permission to access and use their
          Services as set forth in these Terms of Service, provided that:
        </TextSection>
        <List>
          <ListItem>
            You do not alter or modify any part of the Service.
          </ListItem>
          <ListItem>
            You agree not to use or launch any automated system, including
            without limitation, "robots," "spiders," or "offline readers," that
            accesses the Service in a manner that attempts to forge the presence
            of a user accessing the site.
          </ListItem>
          <ListItem>
            You agree not to collect or harvest any personally identifiable
            information, including user account names, from the Service, nor to
            use the communication systems provided by the Service for any
            commercial solicitation purposes.
          </ListItem>
          <ListItem>
            You agree not to use the Services to upload, store, or process
            sensitive personal information except as expressly permitted by
            these Terms and applicable laws.
          </ListItem>
          <ListItem>
            In your use of the Service, you will comply with all applicable
            laws, including landlord-tenant laws, fair housing laws, and other
            property management regulations applicable to your jurisdiction.
          </ListItem>
          <ListItem>
            {APP_NAME} reserves the right to discontinue or otherwise alter or
            modify any aspect of the Service at any time.
          </ListItem>
        </List>

        <Header>Your Content and Conduct</Header>
        <List>
          <ListItem>
            As a {APP_NAME} account holder you may submit Content to certain{" "}
            {APP_NAME} Services, including property information, tenant data,
            lease documents, and financial records, as well as questions,
            responses, and uploads submitted to the AI assistant.
          </ListItem>
          <ListItem>
            You shall be solely responsible for your own Content and the
            consequences of submitting and publishing your Content on a
            {APP_NAME} Service.
          </ListItem>
          <ListItem>
            For landlords and property managers, you represent and warrant that
            you have obtained all necessary tenant authorizations, consents, and
            permissions required by applicable law before uploading or inputting
            any tenant information into the Services. You are solely responsible
            for compliance with tenant consent requirements, privacy laws, and
            professional standards related to your use of the Services.
          </ListItem>
          <ListItem>
            In order to operate the Service, we must obtain from you certain
            license rights in your submitted Content so that actions we take in
            operating the Service are not considered legal violations.
            Accordingly, by using the Service and submitting your Content, you
            grant us a license to access, use, host, cache, store, reproduce,
            transmit, display, publish, distribute, and modify your Content. You
            agree that these rights and licenses are royalty free, transferable,
            sub-licensable, worldwide and irrevocable, and include a right for
            us to make your Content available to, and pass these rights along
            to, others with whom we have contractual relationships related to
            the provision of the Services, and to otherwise permit access to or
            disclose your Content to third parties if we determine such access
            is necessary to comply with our legal obligations. To the fullest
            extent permitted by applicable law, the Company reserves the right,
            and has absolute discretion, to remove, screen, edit, or delete any
            of your Content at any time, for any reason, and without notice.
          </ListItem>
          <ListItem>
            Information submitted to the AI assistant, including questions,
            responses, and any uploaded documents, may be used to improve the
            quality and accuracy of the assistant and the Services. All such
            data will be handled in accordance with our Privacy Policy and
            applicable laws.
          </ListItem>
        </List>

        <Header>Document Templates &amp; Legal Forms</Header>
        <TextSection>
          {APP_NAME} provides technology for document automation. We are not a
          law firm. Lease templates and other form documents are provided for
          informational purposes only. Users must have documents reviewed by
          their own legal counsel before use or execution. Use of our document
          automation features does not create an attorney-client relationship
          and does not constitute legal advice.
        </TextSection>

        <Header>Legal Disclaimer</Header>
        <List>
          <ListItem>
            {APP_NAME} provides a comprehensive AI-powered platform for property
            management that includes multiple components, each with specific
            limitations and disclaimers:
          </ListItem>
          <ListItem>
            <strong>No Legal Advice:</strong> Any information, guidance, or
            answers surfaced by the platform (including the AI assistant, help
            content, emails, or messages) that touch on laws, regulations,
            notices, or procedures are provided for general informational
            purposes only and are not legal advice. {APP_NAME} is not a law firm
            and does not provide legal services. Use of the Services does not
            create an attorney-client relationship. Landlords and property
            managers should consult a qualified attorney licensed in their
            jurisdiction for advice about their specific situation.
          </ListItem>
          <ListItem>
            <strong>AI Technology Disclaimer:</strong> Our platform utilizes
            artificial intelligence and machine learning technologies to provide
            assistant responses, generate messages and summaries, and analyze
            data. While our AI systems are trained on extensive datasets and
            continuously improved, they are not infallible and should not be
            relied upon as the sole basis for legal or business decisions. AI
            responses and generated content should always be reviewed and
            validated by qualified professionals.
          </ListItem>
          <ListItem>
            <strong>AI assistant, Leo, and tenant messaging disclaimer:</strong>{" "}
            Our AI features (including Leo, OpsChat tenant- and prospect-facing
            chat, triage and your AI First Responder, and in-dashboard assistants) are
            designed to provide general information and operational support. They
            do not provide legal advice. All information they surface should be
            reviewed and confirmed by qualified professionals when legal,
            financial, tax, or safety issues are involved. You should not rely
            solely on AI outputs for legal decisions, tenant decisions, or
            investment decisions.
          </ListItem>
          <ListItem>
            <strong>Document Automation:</strong> Lease and form templates
            generated by the platform are administrative tools for document
            management and are provided for convenience only. They are not a
            substitute for advice from a licensed attorney. You are responsible
            for having all documents reviewed by your own legal counsel to
            ensure they meet your specific legal requirements and local
            ordinances.
          </ListItem>
          <ListItem>
            <strong>Analytics and Dashboard Disclaimer:</strong> The analytics,
            metrics, and performance data provided through our dashboard are for
            informational purposes only and should not be considered as
            guarantees of future performance or outcomes. Property performance
            metrics, ROI calculations, and market analysis are based on
            historical data and may not predict future results.
          </ListItem>
          <ListItem>
            <strong>Deal Analyzer and Investment Recommendations:</strong> The
            Deal Analyzer feature (including DSCR estimates, rent and debt
            service estimates, and any recommendations regarding offer price or
            how to proceed) is provided for informational purposes only. It does
            not constitute investment, financial, or legal advice. We do not
            guarantee that you will obtain a property, that any deal will close,
            that a property will perform as estimated, or any particular
            outcome. Estimates are based on AI inference and available data and
            may be inaccurate. You are solely responsible for your own due
            diligence, underwriting, and investment decisions. Use of the Deal
            Analyzer does not create an advisory or fiduciary relationship.
          </ListItem>
          <ListItem>
            <strong>Tax Preparation Disclaimer:</strong> The tax preparation
            support feature is a convenience tool only. {APP_NAME} does not
            provide tax advice, accounting services, or certified public
            accountant (CPA) services. We do not guarantee that data transmitted
            to your CPA is complete, accurate, or suitable for tax filing. You
            are solely responsible for reviewing all information with your CPA
            before using it for tax purposes. {APP_NAME} is not liable for any
            tax consequences, penalties, or errors arising from your use of the
            tax preparation feature or from any data transmitted to your CPA.
            Your CPA is an independent third party, and we have no control over
            their services or advice.
          </ListItem>
          <ListItem>
            <strong>Platform-Wide Legal Disclaimer:</strong> No component of the{" "}
            {APP_NAME} platform is intended to provide legal advice or replace
            professional legal counsel. All platform components are designed to
            support and enhance the property management process, but should
            never replace professional legal judgment or direct attorney-client
            interaction.
          </ListItem>
          <ListItem>
            Property managers and landlords should use all {APP_NAME} platform
            components as supplementary tools only and not as a replacement for
            professional legal judgment. Users should always consult with their
            attorney about the legal implications, compliance requirements, and
            proper procedures for any property management activities before
            making decisions.
          </ListItem>
          <ListItem>
            {APP_NAME} does not practice law or provide legal services. The
            responsibility for the use of any platform component in property
            management rests solely with the user. Users must ensure they have
            appropriate licensing and credentials for any property management
            activities they perform.
          </ListItem>
          <ListItem>
            By using our Services, users acknowledge that they understand all
            platform components are for informational and management support
            purposes only, and that they will rely on their attorney's
            professional judgment for all legal decisions.
          </ListItem>
        </List>

        <Header>Termination</Header>
        <List>
          <ListItem>
            You have the right to opt out of the Services at anytime by deleting
            your account (can be found within your Profile)
          </ListItem>
          <ListItem>
            Users, who violate these Terms of Service, tamper with the operation
            of any program, or engage in any conduct that is detrimental or
            unfair to {APP_NAME}, the program or any other users (in each case
            as determined by {APP_NAME}' sole discretion) are subject to
            suspension or cancellation of their user account(s).
          </ListItem>
          <ListItem>
            If your account is terminated or suspended, we will ensure the
            secure handling of any tenant information in accordance with
            applicable privacy laws and regulations.
          </ListItem>
          <ListItem>
            {APP_NAME} will terminate a user's access to the Service if, under
            appropriate circumstances, the user is determined to be a repeat
            infringer.
          </ListItem>
          <ListItem>
            {APP_NAME} reserves the right to decide whether Content violates
            these Terms of Service for reasons other than copyright
            infringement, such as, but not limited to, pornography, obscenity,
            or excessive length. {APP_NAME} may at any time, without prior
            notice and in its sole discretion, remove such Content and/or
            terminate a user's account for submitting such material in violation
            of these Terms of Service.
          </ListItem>
        </List>

        <Header>Data Security</Header>
        <List>
          <ListItem>
            {APP_NAME} implements technical, administrative, and physical
            safeguards designed to protect your information, including tenant
            data if applicable, in accordance with industry standards and
            applicable laws.
          </ListItem>
          <ListItem>
            Our data security measures include, but are not limited to,
            encryption of data in transit and at rest, access controls, regular
            security assessments, and staff training on data protection.
          </ListItem>
          <ListItem>
            Despite our security measures, no system can be 100% secure, and we
            cannot guarantee the absolute security of your information. You are
            responsible for maintaining the security of your account credentials
            and for any activity that occurs under your account.
          </ListItem>
          <ListItem>
            In the event of a security breach affecting your personal
            information or tenant data, we will notify you in accordance with
            applicable laws and regulations.
          </ListItem>
        </List>

        <Header>Warranties and Disclaimers</Header>
        <List>
          <ListItem>
            Other than as expressly set out in these terms or in additional
            terms, {APP_NAME} will not make any specific promises about the
            Service. For example, {APP_NAME} will not make any commitments about
            the content within the service, the specific function of the Service
            or their reliability, availability, or ability to meet your needs.
          </ListItem>
          <ListItem $capitalize={true}>
            {APP_NAME} PROVIDES THE SERVICES "AS IS" AND "AS AVAILABLE." THE
            ACCURACY OF AI OUTPUTS AND GUIDANCE IS DEPENDENT ON MULTIPLE
            FACTORS, INCLUDING THE QUALITY OF INFORMATION PROVIDED, PROPERTY
            CHARACTERISTICS, AND THE COMPLEXITY OF THE SITUATION. {APP_NAME}{" "}
            DOES NOT GUARANTEE THAT ANY OUTPUTS WILL BE COMPLETE, ERROR-FREE, OR
            COMPLIANT WITH ALL APPLICABLE LAWS OR REGULATIONS.
          </ListItem>
          <ListItem>
            Some jurisdictions provide for certain warranties, like the implied
            warranty of merchantability, fitness for a particular purpose and
            non-infringement. To the extent permitted by law, we exclude all
            warranties.
          </ListItem>
        </List>

        <Header>Customer Support and Service Hours</Header>
        <List>
          <ListItem>
            {APP_NAME} provides customer support to assist Users with
            platform-related questions, technical issues, and general inquiries.
            Support is available through in-app messaging and email at{" "}
            {APP_SUPPORT_EMAIL}.
          </ListItem>
          <ListItem>
            <strong>Support Availability:</strong> Our support team is available
            daily to respond to User inquiries. However, please note that
            response times may be extended during the period from Friday evening
            (sundown) through Saturday evening (sundown) due to religious
            observance. We appreciate your understanding and patience during
            these times.
          </ListItem>
          <ListItem>
            During periods of limited availability, urgent technical issues will
            still be monitored, but non-urgent inquiries may experience delayed
            responses. We strive to respond to all support requests within 24-48
            business hours.
          </ListItem>
          <ListItem>
            Outside of support hours, Users may email {APP_SUPPORT_EMAIL} or use
            the Contact page on our website; responses will follow the timelines
            above.
          </ListItem>
        </List>

        <Header>Limitation of Liability</Header>
        <TextSection>
          The Service will be governed, construed and interpreted under the laws
          of the United States. Members agree to be bound by these Terms of
          Service and by the decisions of {APP_NAME}, which are final.{" "}
          {APP_NAME} reserves the right to suspend or cancel user accounts and
          any benefits should viruses, bugs, unauthorized human intervention or
          other causes beyond our control affect the administration, security or
          proper handling of the benefits or {APP_NAME} otherwise becomes (as
          determined in its sole discretion) incapable of running the program as
          planned. In case of bankruptcy, {APP_NAME} reserves the right to deny
          all benefits and obligations that it may have.
        </TextSection>
        <TextSection $capitalize={true}>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {APP_NAME} WILL NOT
          BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
          PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES
          FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES,
          RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE
          THE SERVICES. THIS LIMITATION APPLIES TO ALL FEATURES INCLUDING TAX
          PREPARATION SUPPORT AND THE DEAL ANALYZER, AND SPECIFICALLY INCLUDES:
          (A) ANY TAX PENALTIES, INTEREST, AUDIT COSTS, OR OTHER CONSEQUENCES
          ARISING FROM DATA TRANSMITTED TO YOUR CPA OR FROM YOUR RELIANCE ON
          SUCH DATA, AND (B) ANY LOSSES ARISING FROM YOUR RELIANCE ON DEAL
          ANALYZER ESTIMATES OR RECOMMENDATIONS, INCLUDING FAILURE TO OBTAIN A
          PROPERTY, POOR INVESTMENT OUTCOMES, OR ANY DECISIONS MADE IN RELIANCE
          ON THE ANALYSIS.
        </TextSection>
        <TextSection $capitalize={true}>
          WITHOUT LIMITING THE FOREGOING, IN NO CASE SHALL THE LIABILITY OF{" "}
          {APP_NAME} EXCEED THE GREATER OF ONE HUNDRED DOLLARS ($100) OR THE
          AMOUNT YOU HAVE PAID {APP_NAME} IN THE PAST SIX MONTHS FOR THE
          SERVICES GIVING RISE TO THE CLAIM.
        </TextSection>
        <TextSection>
          THE FOREGOING LIMITATION OF LIABILITY SHALL APPLY TO THE FULLEST
          EXTENT PERMITTED BY LAW IN THE APPLICABLE JURISDICTION AND SHALL
          SURVIVE TERMINATION OF YOUR ACCOUNT OR THE SERVICES.
        </TextSection>
        <TextSection>
          In all cases, {APP_NAME} will not be liable for any loss or damage
          that is not reasonably foreseeable.
        </TextSection>
        <TextSection>
          In case of any conflict or inconsistency, further agreements between
          you and {APP_NAME} supersede these Terms of Service.
        </TextSection>

        <Header>Intellectual Property</Header>
        <TextSection>
          {APP_NAME} owns and retains all proprietary rights in the Services,
          including all software, algorithms, AI technology, user interfaces,
          and other intellectual property. Nothing in these Terms of Service
          grants you any right, title, or interest in or to any {APP_NAME}{" "}
          intellectual property.
        </TextSection>
        <TextSection>
          While you retain ownership of the Content you upload to the Services,
          you grant {APP_NAME} the rights needed to operate and improve the
          Services as described in these Terms.
        </TextSection>
        <TextSection>
          For landlords and property managers, you understand that platform
          outputs (including messages, summaries, and other content generated
          from your inputs) may constitute derivative works. You are responsible
          for obtaining appropriate permissions for any use of such outputs with
          tenants or for other purposes.
        </TextSection>

        <Header>Governing Law and Dispute Resolution</Header>
        <TextSection>
          These Terms of Service shall be governed by the laws of the United
          States and the state of Missouri, without regard to its conflict of
          law provisions.
        </TextSection>
        <TextSection>
          Any dispute arising out of or relating to these Terms or the Services
          shall first be attempted to be resolved through good faith
          negotiation. If such negotiation fails, any controversy or claim shall
          be submitted to binding arbitration in accordance with the rules of
          the American Arbitration Association. The location of arbitration
          shall be St. Louis, Missouri.
        </TextSection>
        <TextSection>
          Notwithstanding the foregoing, either party may seek injunctive or
          other equitable relief to protect its intellectual property rights or
          confidential information in any court of competent jurisdiction.
        </TextSection>

        <Header>Contact</Header>
        <TextSection>
          For questions about these Terms of Service, send an email to{" "}
          <a
            href={`mailto:${APP_SUPPORT_EMAIL}`}
            rel="noreferrer"
            target="_blank"
          >
            {APP_SUPPORT_EMAIL}
          </a>
          .
        </TextSection>
      </TextContainer>
    </Container>
  );
}

const Container = styled.div`
  color: black;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  text-align: left;
  width: 80%;
  margin: auto;
  margin-bottom: ${(props) => (props.$mustAcceptTos ? "0" : "40")}px;
  margin-top: 3rem;

  a {
    color: #1a73e8;
    font-weight: 500;
    text-decoration: none;
  }

  a:hover {
    color: #0d47a1;
    text-decoration: underline;
  }
`;

const TextContainer = styled.div`
  overflow-y: ${(props) => (props.$mustAcceptTos ? "auto" : "unset")};
  overflow-x: ${(props) => (props.$mustAcceptTos ? "hidden" : "unset")};
  height: ${(props) => (props.$mustAcceptTos ? "500px" : "auto")};
  max-height: ${(props) => (props.$mustAcceptTos ? "500px" : "auto")};
  box-shadow: ${(props) =>
    props.$mustAcceptTos ? "0px 4px 0px 0px rgba(0, 0, 0, 0.1)" : "none"};
  padding-right: 5%;
  padding-bottom: 32px;

  @media screen and (max-height: 825px) {
    height: ${(props) => (props.$mustAcceptTos ? "400px" : "auto")};
    max-height: ${(props) => (props.$mustAcceptTos ? "400px" : "auto")};
  }
`;

const Header = styled.div`
  font-size: 13px;
  font-weight: bold;
  margin-top: 24px;
`;

const MainHeader = styled.div`
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 18px;
`;

const List = styled.ul`
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  margin-top: 18px;
`;

const ListItem = styled.li`
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  margin-top: 10px;
  text-transform: ${(props) => (props.$capitalize ? "uppercase" : "none")};
`;

const TextSection = styled.div`
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20.8px;
  letter-spacing: 0.2px;
  margin-top: 18px;
  text-transform: ${(props) => (props.$capitalize ? "uppercase" : "none")};
`;
